import CacheByArgumentStrings from './mem/cache-by-argument-strings';
import mfac from './material-factory';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Mesh, Object3D } from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';


/**
 * The ModelLoader loads and caches Object3D instances from OBJ and GLTF files.
 */
export class ModelLoader {

    cache: CacheByArgumentStrings< Promise<Object3D> >;
    objLoader: OBJLoader;
    gltfLoader: GLTFLoader;

    private static instance: ModelLoader;

    constructor() {
        this.cache = new CacheByArgumentStrings();
        this.objLoader = new OBJLoader();
        this.gltfLoader = new GLTFLoader();
    }

    /**
     * The ModelLoader may be used as a singleton
     */
    static getInstance() {
        if(! this.instance){
            this.instance = new this();
        }
        return this.instance;
    }

    private objLoad(url: string) {
        let promise: Promise<Object3D> = new Promise((resolve, reject) => {
            this.objLoader.load(
                url,
                // Load completed
                object => {
                    object.name = url;
                    object.traverse(child => {
                        if(child instanceof Mesh) {
                            child.geometry.name = url;
                            child.castShadow = child.receiveShadow = true;
                        }
                    });
                    resolve(object);
                },
                null, // progress callbacks is unsed/null
                error => {
                    console.log(error);
                    reject(error);
                }
            )
        });
        this.cache.put(promise, url);
        return promise;
    }

    private gltfLoad(url: string) {
        let promise: Promise<Object3D> = new Promise((resolve, reject) => {
            this.gltfLoader.load(
                url,
                // Load completed
                gltf => {
                    const object = gltf.scene;
                    object.name = url;
                    object.traverse(child => {
                        if(child instanceof Mesh) {
                            child.geometry.name = url;
                            child.castShadow = child.receiveShadow = true;

                            if(child.material && 
                               'cpsFactoryMaterial' in child.material.userData)
                            {
                                child.material =
                                    mfac.make(child.material.userData.cpsFactoryMaterial);
                            }
                        }
                    });
                    resolve(object);
                },
                null, // progress callbacks is unsed/null
                error => {
                    console.log(error);
                    reject(error);
                }
            )
        });
        this.cache.put(promise, url);
        return promise;
    }

    /**
     * Returns a Promise for the Object3D representing the top level object in
     * the loaded OBJ or GLTF file.
     */
    async get(url: string) {
        let promise = this.cache.get(url);
        if(promise == null) {
            const fileType = url.split('.').pop();
            if (fileType == "obj") {
                promise = this.objLoad(url);
            } else if (fileType == "gltf" || fileType == "glb") {
                promise = this.gltfLoad(url);
            }
        }
        let obj = await promise;
        return obj?.clone();
    }
}
export default ModelLoader;
